import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// import { X, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  X,
  Download,
  Copy,
  Loader,
  Check,
  FileCode,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import * as XLSX from 'xlsx';
import mammoth from 'mammoth';
import { cn } from '@/lib/utils';
// import MarkdownRenderer from './MessageBubble/MarkdownRenderer';
import MarkdownPreviewPane from './MarkdownPreviewPane';
import MonacoCodeEditor from './MonacoCodeEditor';
import { useUIStore } from '@/stores/uiStore';
import { isTextFile } from './MessageBubble/textFileExtensions';

interface FilePreviewProps {
  /** 文件 URL（可选，如果不传则从 store 读取） */
  fileUrl?: string;
  /** 文件名（可选，如果不传则从 store 读取） */
  filename?: string;
  /** 关闭回调（可选，如果不传则使用 store 的 setFilePreview(null)） */
  onClose?: () => void;
  /** 项目 ID，用于保存文件 */
  projectId?: string;
  /** 文件路径，用于保存文件 */
  filePath?: string;
  /** Client ID，用于通过 client 接口保存文件（当 projectId 不可用时） */
  clientId?: string;
  /** 渲染模式：split（分屏）或 overlay（覆盖）或 auto（自动根据设备选择） */
  mode?: 'split' | 'overlay' | 'auto';
  /** 只读模式（禁用编辑功能） */
  readOnly?: boolean;
}

// 🔥 设备检测 Hook：判断是否为移动设备（手机或平板）
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      // 使用多种方式检测移动设备
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileUA =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(
          userAgent
        );
      const isSmallScreen = window.innerWidth <= 1024; // 平板和手机屏幕宽度通常 <= 1024px
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // 综合判断：UA 检测到移动设备，或者是小屏幕触摸设备
      setIsMobile(isMobileUA || (isSmallScreen && isTouchDevice));
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
}

/**
 * VirtualCodeViewer - 虚拟滚动的代码查看器
 * 用于高效渲染大型文件（几万行代码）
 */
interface VirtualCodeViewerProps {
  content: string;
  language: string;
}

function VirtualCodeViewer({ content, language }: VirtualCodeViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const { t } = useTranslation();

  // 将内容分割成行
  const lines = useMemo(() => content.split('\n'), [content]);
  const lineCount = lines.length;

  // 虚拟滚动参数
  const LINE_HEIGHT = 20; // 每行高度（像素）
  const OVERSCAN = 10; // 上下额外渲染的行数
  const LARGE_FILE_THRESHOLD = 500; // 超过这个行数才启用虚拟滚动（降低阈值以提前优化）

  // 是否启用虚拟滚动
  const useVirtualScroll = lineCount > LARGE_FILE_THRESHOLD;

  // 计算可见区域
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / LINE_HEIGHT) - OVERSCAN
  );
  const endIndex = Math.min(
    lineCount,
    Math.ceil((scrollTop + containerHeight) / LINE_HEIGHT) + OVERSCAN
  );

  // 可见的行内容（用于语法高亮）
  const visibleContent = useMemo(() => {
    if (!useVirtualScroll) return content;
    return lines.slice(startIndex, endIndex).join('\n');
  }, [useVirtualScroll, lines, startIndex, endIndex, content]);

  // 监听容器尺寸变化（必须在条件分支之前）
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  };

  // 小文件：直接使用 SyntaxHighlighter
  if (!useVirtualScroll) {
    return (
      <div
        className="h-full overflow-x-auto overflow-y-hidden bg-[#fafafa]"
        ref={containerRef}
      >
        <div className="h-full overflow-y-auto">
          <SyntaxHighlighter
            language={language}
            style={oneLight}
            customStyle={{
              margin: 0,
              padding: '1.5rem',
              fontSize: '0.875rem',
              backgroundColor: '#fafafa',
              minHeight: '100%',
              overflowX: 'visible',
              display: 'inline-block',
              minWidth: '100%',
            }}
            showLineNumbers={true}
            wrapLines={true}
            wrapLongLines={true}
            lineProps={{
              style: {
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                display: 'block',
              },
            }}
          >
            {content}
          </SyntaxHighlighter>
        </div>
      </div>
    );
  }

  // 大文件：使用虚拟滚动 + 语法高亮
  const totalHeight = lineCount * LINE_HEIGHT;
  const offsetY = startIndex * LINE_HEIGHT;

  return (
    <div className="h-full overflow-x-auto overflow-y-hidden bg-[#fafafa]">
      <div
        ref={containerRef}
        className="h-full overflow-y-auto"
        onScroll={handleScroll}
        style={{ position: 'relative' }}
      >
        {/* 占位符，用于撑开滚动条 */}
        <div style={{ height: totalHeight, position: 'relative' }}>
          {/* 可见内容 - 使用 SyntaxHighlighter 渲染 */}
          <div
            style={{
              position: 'absolute',
              top: offsetY,
              left: 0,
              right: 0,
            }}
          >
            <SyntaxHighlighter
              language={language}
              style={oneLight}
              customStyle={{
                margin: 0,
                padding: '0 1.5rem',
                fontSize: '0.875rem',
                backgroundColor: '#fafafa',
                overflowX: 'visible',
                display: 'inline-block',
                minWidth: '100%',
              }}
              showLineNumbers={true}
              startingLineNumber={startIndex + 1}
              wrapLines={true}
              wrapLongLines={true}
              lineProps={{
                style: {
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  display: 'block',
                },
              }}
              lineNumberStyle={{
                minWidth: '3.5em',
                paddingRight: '1em',
                textAlign: 'right',
                userSelect: 'none',
              }}
            >
              {visibleContent}
            </SyntaxHighlighter>
          </div>
        </div>
        {/* 文件信息提示 */}
        <div
          className="sticky bottom-4 right-4 ml-auto mr-4 w-fit px-3 py-1.5 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 shadow-sm"
          style={{ pointerEvents: 'none' }}
        >
          {t('filePreview.largeFileVirtualScrolling', {
            count: lineCount.toLocaleString(),
            defaultValue: `Large file (${lineCount.toLocaleString()} lines) - Virtual scrolling enabled`,
          } as any)}
        </div>
      </div>
    </div>
  );
}

/**
 * VirtualTableViewer - 虚拟滚动的表格查看器
 * 用于高效渲染大型 CSV/Excel 数据表
 */
interface VirtualTableViewerProps {
  data: string[][];
}

function VirtualTableViewer({ data }: VirtualTableViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const { t } = useTranslation();
  // 监听容器尺寸变化（必须在所有条件判断之前）
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        {t('filePreview.noDataAvailable')}
      </div>
    );
  }

  const headerRow = data[0] || [];
  const dataRows = data.slice(1);
  const rowCount = dataRows.length;

  // 虚拟滚动参数
  const ROW_HEIGHT = 41; // 每行高度（像素）
  const HEADER_HEIGHT = 45; // 表头高度
  const OVERSCAN = 5; // 上下额外渲染的行数
  const LARGE_TABLE_THRESHOLD = 500; // 超过这个行数才启用虚拟滚动

  const useVirtualScroll = rowCount > LARGE_TABLE_THRESHOLD;

  // 计算可见区域（排除表头）
  const visibleScrollTop = Math.max(0, scrollTop - HEADER_HEIGHT);
  const startIndex = Math.max(
    0,
    Math.floor(visibleScrollTop / ROW_HEIGHT) - OVERSCAN
  );
  const endIndex = Math.min(
    rowCount,
    Math.ceil((visibleScrollTop + containerHeight) / ROW_HEIGHT) + OVERSCAN
  );

  const visibleRows = useVirtualScroll
    ? dataRows.slice(startIndex, endIndex)
    : dataRows;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  };

  // 小表格：直接渲染
  if (!useVirtualScroll) {
    return (
      <div className="h-[calc(100%_-_1rem)] overflow-x-auto overflow-y-hidden bg-white">
        <div ref={containerRef} className="h-full overflow-y-auto">
          <div className="inline-block min-w-full">
            <table className="min-w-full bg-white border-collapse">
              <thead className="bg-gray-100 border-b-2 border-gray-300 sticky top-0 z-10">
                <tr>
                  {headerRow.map((header, index) => (
                    <th
                      key={index}
                      className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 whitespace-nowrap"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {dataRows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className="px-4 py-2.5 text-left text-gray-700 border-r border-gray-200 truncate whitespace-nowrap"
                        title={cell}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // 大表格：使用虚拟滚动
  const totalHeight = HEADER_HEIGHT + rowCount * ROW_HEIGHT;
  const offsetY = HEADER_HEIGHT + startIndex * ROW_HEIGHT;

  return (
    <div className="h-[calc(100%_-_1rem)] overflow-x-auto overflow-y-hidden bg-white">
      <div
        ref={containerRef}
        className="h-full overflow-y-auto"
        onScroll={handleScroll}
        style={{ position: 'relative' }}
      >
        <div className="inline-block min-w-full">
          {/* 占位符 */}
          <div style={{ height: totalHeight, position: 'relative' }}>
            {/* 固定表头 */}
            <div
              className="sticky top-0 z-10 border-b-2 border-gray-300"
              style={{
                height: HEADER_HEIGHT,
                backgroundColor: '#f3f4f6', // bg-gray-100
              }}
            >
              <div className="flex">
                {headerRow.map((header, index) => (
                  <div
                    key={index}
                    className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 whitespace-nowrap"
                    style={{
                      width: '150px',
                      minWidth: '150px',
                      flex: '0 0 150px', // 固定宽度，不伸缩
                      backgroundColor: '#f3f4f6',
                    }}
                  >
                    {header}
                  </div>
                ))}
              </div>
            </div>

            {/* 可见行 */}
            <div
              style={{
                position: 'absolute',
                top: offsetY,
                left: 0,
                right: 0,
              }}
            >
              {visibleRows.map((row, index) => {
                const actualIndex = startIndex + index;
                return (
                  <div
                    key={actualIndex}
                    className={`flex ${actualIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    style={{
                      height: ROW_HEIGHT,
                      borderBottom: '1px solid #e5e7eb',
                    }}
                  >
                    {/* 确保每行渲染与表头相同数量的单元格 */}
                    {headerRow.map((_, cellIndex) => (
                      <div
                        key={cellIndex}
                        className="px-4 py-2.5 text-left text-gray-700 border-r border-gray-200 truncate whitespace-nowrap"
                        title={row[cellIndex] ?? ''}
                        style={{
                          width: '150px',
                          minWidth: '150px',
                          flex: '0 0 150px', // 固定宽度，与表头一致
                        }}
                      >
                        {row[cellIndex] ?? ''}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 文件类型映射
const FILE_TYPES = {
  // 代码文件
  code: [
    '.js',
    '.mjs',
    '.jsx',
    '.ts',
    '.tsx',
    '.py',
    '.java',
    '.cpp',
    '.c',
    '.cs',
    '.php',
    '.rb',
    '.go',
    '.rs',
    '.swift',
    '.kt',
    '.scala',
    '.r',
    '.m',
    '.sh',
    '.bash',
    '.zsh',
    '.fish',
    '.ps1',
    '.bat',
    '.cmd',
    '.vbs',
    '.sql',
    '.pl',
    '.lua',
    '.vim',
    '.el',
    '.clj',
    '.ex',
    '.exs',
    '.erl',
    '.hrl',
    '.fs',
    '.fsx',
    '.ml',
    '.mli',
    '.hs',
    '.lhs',
    '.dart',
    '.jl',
    '.nim',
    '.cr',
    '.v',
    '.sv',
    '.svh',
    '.vh',
    '.vhd',
    '.vhdl',
    // 样式文件
    '.css',
    '.scss',
    '.sass',
    '.less',
    '.styl',
    '.stylus',
  ],
  // SAS 文件
  sas: ['.sas'],
  // 数据文件（包括 CSV 和 Excel）
  data: [
    '.rda',
    '.rdata',
    '.rds',
    '.csv',
    '.tsv',
    '.xlsx',
    '.xls',
    '.json',
    '.xml',
    '.yaml',
    '.yml',
    '.toml',
    '.ini',
    '.conf',
    '.config',
  ],
  // 标记语言
  markup: ['.html', '.htm', '.vue', '.svelte', '.astro'],
  // Markdown
  markdown: ['.md', '.markdown', '.mdown', '.mkd', '.mkdn', '.mdx'],
  // 文本文件
  text: ['.txt', '.log', '.out', '.err', '.rtf'],
  // PDF（不在这里处理，保持原有逻辑）
  pdf: ['.pdf'],
  // Office 文件（直接下载）
  office: [
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.odt',
    '.ods',
    '.odp',
  ],
  // 图片文件
  image: [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.webp',
    '.svg',
    '.ico',
    '.tiff',
    '.tif',
  ],
};

const getFileType = (filename: string): string => {
  const lower = filename.toLowerCase();
  const ext = '.' + lower.split('.').pop();

  if (FILE_TYPES.code.includes(ext)) return 'code';
  if (FILE_TYPES.sas.includes(ext)) return 'sas';
  if (FILE_TYPES.data.includes(ext)) return 'data';
  if (FILE_TYPES.markup.includes(ext)) return 'markup';
  if (FILE_TYPES.markdown.includes(ext)) return 'markdown';
  if (FILE_TYPES.text.includes(ext)) return 'text';
  if (FILE_TYPES.pdf.includes(ext)) return 'pdf';
  if (FILE_TYPES.office.includes(ext)) return 'office';
  if (FILE_TYPES.image.includes(ext)) return 'image';

  // 🔥 使用 TEXT_FILE_EXTENSIONS 作为回退检查，支持更多文本文件类型的编辑
  if (isTextFile(filename)) return 'text';

  return 'unknown';
};

const getLanguageFromFilename = (filename: string): string => {
  const lower = filename.toLowerCase();
  const ext = lower.split('.').pop() || '';

  const langMap: Record<string, string> = {
    js: 'javascript',
    jsx: 'jsx',
    ts: 'typescript',
    tsx: 'tsx',
    py: 'python',
    java: 'java',
    cpp: 'cpp',
    c: 'c',
    cs: 'csharp',
    php: 'php',
    rb: 'ruby',
    go: 'go',
    rs: 'rust',
    swift: 'swift',
    kt: 'kotlin',
    scala: 'scala',
    r: 'r',
    sas: 'sas',
    sh: 'bash',
    bash: 'bash',
    zsh: 'bash',
    fish: 'bash',
    ps1: 'powershell',
    bat: 'batch',
    cmd: 'batch',
    sql: 'sql',
    html: 'html',
    htm: 'html',
    vue: 'vue',
    xml: 'xml',
    json: 'json',
    yaml: 'yaml',
    yml: 'yaml',
    md: 'markdown',
    txt: 'text',
  };

  return langMap[ext] || ext;
};

// 🔥 新增：检测文件编码并解码（支持 UTF-8, UTF-16LE, UTF-16BE 等）
const decodeTextWithAutoDetect = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);

  // 检测 BOM (Byte Order Mark)
  // UTF-16LE: FF FE (255, 254)
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    // 排除 UTF-32LE: FF FE 00 00
    if (bytes.length >= 4 && bytes[2] === 0x00 && bytes[3] === 0x00) {
      return new TextDecoder('utf-32le').decode(buffer);
    }
    return new TextDecoder('utf-16le').decode(buffer);
  }

  // UTF-16BE: FE FF (254, 255)
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(buffer);
  }

  // UTF-8 BOM: EF BB BF (239, 187, 191)
  if (
    bytes.length >= 3 &&
    bytes[0] === 0xef &&
    bytes[1] === 0xbb &&
    bytes[2] === 0xbf
  ) {
    return new TextDecoder('utf-8').decode(buffer);
  }

  // UTF-32BE: 00 00 FE FF
  if (
    bytes.length >= 4 &&
    bytes[0] === 0x00 &&
    bytes[1] === 0x00 &&
    bytes[2] === 0xfe &&
    bytes[3] === 0xff
  ) {
    return new TextDecoder('utf-32be').decode(buffer);
  }

  // 默认使用 UTF-8（没有 BOM 的情况）
  return new TextDecoder('utf-8').decode(buffer);
};

export default function FilePreview({
  fileUrl: propsFileUrl,
  filename: propsFilename,
  onClose: propsOnClose,
  projectId: propsProjectId,
  filePath: propsFilePath,
  clientId: propsClientId,
  mode = 'auto',
  readOnly = false,
}: FilePreviewProps) {
  // 国际化
  const { t } = useTranslation();

  // 🔥 从 store 读取文件预览状态（单例模式）
  const { filePreview, setFilePreview } = useUIStore();

  // 🔥 监听 AI 对话流式输出状态，用于对话结束后自动刷新/关闭文件预览
  const isStreamingActive = useUIStore(state => state.isStreamingActive);

  // 🔥 设备检测
  const isMobile = useIsMobile();

  // 🔥 优先使用 props，否则从 store 读取
  const fileUrl = propsFileUrl ?? filePreview?.fileUrl ?? '';
  const filename = propsFilename ?? filePreview?.filename ?? '';
  const projectId = propsProjectId ?? filePreview?.projectId;
  const filePath = propsFilePath ?? filePreview?.filePath;
  const clientId = propsClientId ?? filePreview?.clientId;

  // 🔥 关闭处理：优先使用 props 回调，否则清空 store
  const handleClose = useCallback(() => {
    if (propsOnClose) {
      propsOnClose();
    } else {
      setFilePreview(null);
    }
  }, [propsOnClose, setFilePreview]);

  // 🔥 所有 hooks 必须在条件语句之前调用（React 规则）
  const [content, setContent] = useState<string>('');
  const [rawContent, setRawContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [width, setWidth] = useState(50); // 百分比宽度
  const [isResizing, setIsResizing] = useState(false);
  const resizeDragRef = useRef<{ startX: number; startWidth: number } | null>(
    null
  );
  const [csvData, setCsvData] = useState<string[][] | null>(null);
  const [wordHtml, setWordHtml] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showRawMarkdown, setShowRawMarkdown] = useState(false);
  const [showRawCsv, setShowRawCsv] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 🔥 AI 对话结束后自动刷新文件预览的状态
  const [refreshKey, setRefreshKey] = useState(0);
  const prevStreamingRef = useRef(false);

  // 🔥 带缓存破坏参数的文件 URL，用于 PDF/图片等直接通过 src 加载的文件
  // 当 refreshKey 变化时（AI 对话结束后），追加参数强制浏览器重新加载
  const displayFileUrl = useMemo(() => {
    if (refreshKey > 0 && fileUrl) {
      const separator = fileUrl.includes('?') ? '&' : '?';
      return `${fileUrl}${separator}_refresh=${refreshKey}`;
    }
    return fileUrl;
  }, [fileUrl, refreshKey]);

  // 🔥 移动设备默认全屏预览
  useEffect(() => {
    if (isMobile) {
      setIsFullscreen(true);
    }
  }, [isMobile]);

  // 🔥 AI 对话结束后自动刷新/关闭文件预览
  // 当 isStreamingActive 从 true 变为 false 时，说明 AI 对话结束
  // 如果预览的文件属于当前项目，则触发刷新以检测文件是否被修改、删除或重命名
  useEffect(() => {
    const wasStreaming = prevStreamingRef.current;
    prevStreamingRef.current = isStreamingActive;

    // 检测流式输出结束（从 true -> false）
    if (wasStreaming && !isStreamingActive) {
      // 只有当预览的文件属于某个项目时才触发刷新
      if (projectId && filePath) {
        console.log('🔄 AI 对话结束，触发文件预览刷新检查:', {
          projectId,
          filePath,
          filename,
        });
        setRefreshKey(prev => prev + 1);
      }
    }
  }, [isStreamingActive, projectId, filePath, filename]);

  // 图片缩放相关状态
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const fileType = getFileType(filename);
  const language = getLanguageFromFilename(filename);

  // 🔥 计算实际渲染模式
  // - PC端（非移动设备）: 使用分屏模式 (split)
  // - 移动设备（手机/平板）: 使用弹窗模式 (overlay)，宽度占满除侧边栏外的区域
  const actualMode = useMemo(() => {
    if (mode === 'auto') {
      return isMobile ? 'overlay' : 'split';
    }
    return mode;
  }, [mode, isMobile]);

  // 内存化JSON格式化结果，避免重复解析
  const formattedJsonContent = useMemo(() => {
    if (fileType === 'data' && language === 'json' && content) {
      try {
        return JSON.stringify(JSON.parse(content), null, 2);
      } catch (e) {
        return content; // 返回原始内容
      }
    }
    return null;
  }, [content, fileType, language]);

  useEffect(() => {
    // 🔥 判断是否为 AI 对话结束后的自动刷新操作
    const isRefresh = refreshKey > 0;

    // 🔥 辅助函数：检测 API 返回的 JSON 错误响应（服务端对不存在的文件返回 200 + JSON 错误体）
    const isApiErrorResponse = (text: string): boolean => {
      try {
        const json = JSON.parse(text);
        // 匹配 {"code":50109,"message":"File xxx does not exist"} 等 API 错误格式
        if (json && typeof json.code === 'number' && json.message) {
          return true;
        }
      } catch {
        // 不是 JSON，说明是正常的文件内容
      }
      return false;
    };

    // 如果不需要加载，则跳过
    if (fileType === 'pdf' || fileType === 'unknown') {
      // 🔥 如果是刷新操作（AI 对话结束后），需要检查文件是否还存在
      if (isRefresh && projectId && filePath) {
        // 🔥 使用 GET 请求并检查 Content-Type，因为 API 对不存在的文件返回 200 + JSON 错误体
        fetch(fileUrl)
          .then(async res => {
            const contentType = res.headers.get('content-type') || '';
            // 如果返回 JSON 类型，说明是 API 错误响应（文件不存在）
            if (contentType.includes('application/json')) {
              try {
                const json = await res.json();
                if (json.code || json.error) {
                  console.log('🔥 文件已被删除或重命名，关闭预览:', filePath);
                  handleClose();
                }
              } catch {
                // JSON 解析失败，忽略
              }
            }
            // 否则文件存在，displayFileUrl 的缓存破坏参数会处理 iframe/img 的刷新
          })
          .catch(() => {
            // 🔥 网络错误不关闭预览，避免误关
            console.warn('🔥 文件访问网络错误，保留预览:', filePath);
          });
        return;
      }
      // 🔥 清除之前的内容状态，避免显示旧文件内容
      setContent('');
      setRawContent('');
      setCsvData(null);
      setWordHtml(null);
      setError('');
      setLoading(false);
      return;
    }

    const fetchContent = async () => {
      try {
        // 🔥 刷新时不显示 loading 状态，避免界面闪烁
        if (!isRefresh) {
          setLoading(true);
          // 🔥 立即清除所有内容状态，确保不显示旧文件内容
          setContent('');
          setRawContent('');
          setCsvData(null);
          setWordHtml(null);
          setError('');
        }

        const response = await fetch(fileUrl);
        if (!response.ok) {
          // 🔥 如果是刷新操作且返回 404，说明文件被删除或重命名，关闭预览
          if (
            isRefresh &&
            (response.status === 404 || response.status === 403)
          ) {
            console.log('🔥 文件已被删除或重命名，关闭预览:', filePath);
            handleClose();
            return;
          }
          throw new Error(`Failed to load file: ${response.statusText}`);
        }

        const isExcel =
          filename.toLowerCase().endsWith('.xlsx') ||
          filename.toLowerCase().endsWith('.xls');
        const isCsv =
          filename.toLowerCase().endsWith('.csv') ||
          filename.toLowerCase().endsWith('.tsv');
        const isWord =
          filename.toLowerCase().endsWith('.docx') ||
          filename.toLowerCase().endsWith('.doc');

        // 处理 Word 文件
        if (fileType === 'office' && isWord) {
          const arrayBuffer = await response.arrayBuffer();
          try {
            // 配置 mammoth 以保留更多样式，包括缩进和对齐
            const result = await mammoth.convertToHtml(
              { arrayBuffer },
              {
                styleMap: [
                  // 标题映射
                  "p[style-name='Heading 1'] => h1:fresh",
                  "p[style-name='Heading 2'] => h2:fresh",
                  "p[style-name='Heading 3'] => h3:fresh",
                  "p[style-name='Heading 4'] => h4:fresh",
                  "p[style-name='Heading 5'] => h5:fresh",
                  "p[style-name='Heading 6'] => h6:fresh",
                  // 中文标题映射
                  "p[style-name='标题1'] => h1:fresh",
                  "p[style-name='标题2'] => h2:fresh",
                  "p[style-name='标题3'] => h3:fresh",
                  "p[style-name='标题4'] => h4:fresh",
                  "p[style-name='标题5'] => h5:fresh",
                  "p[style-name='标题6'] => h6:fresh",
                  // 列表映射
                  "p[style-name='List Paragraph'] => p.list-paragraph:fresh",
                  // 普通段落
                  "p[style-name='Normal'] => p:fresh",
                ],
                includeDefaultStyleMap: true,
                // 转换图片为 base64
                convertImage: mammoth.images.imgElement(function (image) {
                  return image.read('base64').then(function (imageBuffer) {
                    return {
                      src:
                        'data:' + image.contentType + ';base64,' + imageBuffer,
                    };
                  });
                }),
              }
            );
            // 🔥 给所有链接添加 target="_blank"，使其在新标签页中打开
            const htmlWithTargetBlank = result.value.replace(
              /<a\s+href=/gi,
              '<a target="_blank" rel="noopener noreferrer" href='
            );
            setWordHtml(htmlWithTargetBlank);
            // 设置原始文本用于复制
            const textResult = await mammoth.extractRawText({ arrayBuffer });
            setRawContent(textResult.value);
          } catch (err) {
            console.error('Error converting Word document:', err);
            throw new Error('Failed to convert Word document');
          }
        }
        // 处理 Excel 文件
        else if (fileType === 'office' && isExcel) {
          const arrayBuffer = await response.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const data = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
          }) as string[][];
          setCsvData(data);
        }
        // 处理 CSV 文件
        else if (fileType === 'data' && isCsv) {
          // 🔥 修复：使用编码自动检测，支持 UTF-16 等编码
          const arrayBuffer = await response.arrayBuffer();
          const text = decodeTextWithAutoDetect(arrayBuffer);
          setRawContent(text);
          const workbook = XLSX.read(text, { type: 'string' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const data = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
          }) as string[][];
          setCsvData(data);
        } else {
          // 🔥 修复：使用编码自动检测，支持 UTF-16 等编码
          const arrayBuffer = await response.arrayBuffer();
          const text = decodeTextWithAutoDetect(arrayBuffer);
          // 🔥 刷新时检测 API 返回的 JSON 错误响应（服务端对已删除文件返回 200 + JSON 错误体）
          if (isRefresh && isApiErrorResponse(text)) {
            console.log('🔥 文件已被删除或重命名，关闭预览:', filePath);
            handleClose();
            return;
          }
          setContent(text);
          setRawContent(text);
        }
      } catch (err) {
        console.error('Error loading file:', err);
        // 🔥 刷新操作时如果出错，可能是文件被删除，关闭预览
        if (isRefresh && projectId && filePath) {
          console.log('🔥 刷新时加载文件失败，关闭预览:', filePath);
          handleClose();
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load file');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [
    fileUrl,
    fileType,
    filename,
    refreshKey,
    handleClose,
    projectId,
    filePath,
  ]);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleCopy = async () => {
    try {
      // For Excel office files, raw text content is not available
      const isExcel =
        filename.toLowerCase().endsWith('.xlsx') ||
        filename.toLowerCase().endsWith('.xls');
      if (fileType === 'office' && isExcel) {
        throw new Error('Copy is not supported for Excel files');
      }

      const textToCopy = rawContent ?? '';
      if (!textToCopy) {
        throw new Error('No textual content available to copy');
      }
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Copy failed:', e);
      alert(e instanceof Error ? `Copy failed: ${e.message}` : 'Copy failed.');
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    resizeDragRef.current = { startX: e.clientX, startWidth: width };
    setIsResizing(true);
  };

  // 图片缩放处理 - 使用 passive: false 以允许 preventDefault
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container || fileType !== 'image' || loading) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setImageScale(prev => Math.max(0.1, Math.min(5, prev + delta)));
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [fileType, loading]);

  // 图片拖拽开始
  const handleImageMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (imageScale <= 1) return; // 只有放大时才允许拖拽
    e.preventDefault();
    setIsDraggingImage(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y,
    });
  };

  // 图片拖拽中
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingImage) return;
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDraggingImage(false);
    };

    if (isDraggingImage) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingImage, dragStart]);

  // 重置图片缩放
  const handleImageDoubleClick = () => {
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  // 处理拖拽调整宽度 - 参考 ArtifactViewer 的实现
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeDragRef.current) return;
      const { startX, startWidth } = resizeDragRef.current;
      const windowWidth = window.innerWidth;
      // 增量计算：鼠标向左移动 → 面板变宽，向右移动 → 面板变窄
      const deltaX = e.clientX - startX;
      const deltaPercent = (deltaX / windowWidth) * 100;
      const newWidth = startWidth - deltaPercent;
      // 🔥 限制宽度在 30% 到 60% 之间，避免左侧内容被过度压缩
      const clampedWidth = Math.min(Math.max(newWidth, 30), 60);
      setWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      resizeDragRef.current = null;
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-3">
          <Loader size={32} className="text-blue-500 animate-spin" />
          <div className="text-sm text-gray-500">
            {t('filePreview.loadingFile')}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-red-500 text-sm">{error}</div>
        </div>
      );
    }

    // CSV 表格渲染
    // CSV 渲染 - 支持 Preview 和 Raw 两种模式
    if (csvData) {
      if (showRawCsv) {
        // 🔥 Raw 模式使用 Monaco Editor（显示原始 CSV 文本）
        if ((projectId && filePath) || (clientId && filePath)) {
          return (
            <MonacoCodeEditor
              content={rawContent}
              language="plaintext"
              filename={filename}
              projectId={projectId}
              filePath={filePath}
              clientId={clientId}
              readOnly={readOnly}
              onSave={newContent => {
                // 🔥 保存成功后同步更新 rawContent 并重新解析 csvData
                setRawContent(newContent);
                // 重新解析 CSV 数据
                const workbook = XLSX.read(newContent, { type: 'string' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const data = XLSX.utils.sheet_to_json(worksheet, {
                  header: 1,
                }) as string[][];
                setCsvData(data);
              }}
            />
          );
        }
        // 只读模式
        return (
          <MonacoCodeEditor
            content={rawContent}
            language="plaintext"
            filename={filename}
            readOnly
          />
        );
      }
      // Preview 模式：表格视图
      return <VirtualTableViewer data={csvData} />;
    }

    // Word 文档渲染
    if (wordHtml) {
      return (
        <div className="h-full overflow-auto bg-white">
          <div
            className="word-content p-8 max-w-5xl mx-auto"
            dangerouslySetInnerHTML={{ __html: wordHtml }}
            onClick={e => {
              // 🔥 拦截锚点链接点击，防止 hash router 干扰
              const target = e.target as HTMLElement;
              const anchor = target.closest('a');
              if (anchor) {
                const href = anchor.getAttribute('href');
                if (href && href.startsWith('#')) {
                  e.preventDefault();
                  // 在容器内查找目标元素
                  const container = e.currentTarget;
                  const targetId = href.slice(1); // 移除 # 前缀
                  const targetElement = container.querySelector(
                    `[id="${targetId}"], [name="${targetId}"]`
                  );
                  if (targetElement) {
                    targetElement.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }
                }
              }
            }}
            style={{
              fontFamily:
                'system-ui, -apple-system, "Microsoft YaHei", sans-serif',
              fontSize: '15px',
              lineHeight: '1.8',
              color: '#333',
            }}
          />
          <style>{`
            /* Word 文档样式 - 简洁版，保持原始格式 */
            .word-content h1 { 
              font-size: 2em; 
              font-weight: bold; 
              margin: 1em 0 0.5em 0; 
              color: #1a1a1a;
              line-height: 1.3;
            }
            .word-content h2 { 
              font-size: 1.5em; 
              font-weight: bold; 
              margin: 0.9em 0 0.4em 0; 
              color: #1a1a1a;
              line-height: 1.3;
            }
            .word-content h3 { 
              font-size: 1.17em; 
              font-weight: bold; 
              margin: 0.8em 0 0.4em 0; 
              color: #1a1a1a;
              line-height: 1.3;
            }
            .word-content h4 { 
              font-size: 1em; 
              font-weight: bold; 
              margin: 0.7em 0 0.3em 0; 
              color: #1a1a1a;
            }
            .word-content h5 { 
              font-size: 0.83em; 
              font-weight: bold; 
              margin: 0.6em 0 0.3em 0; 
              color: #1a1a1a;
            }
            .word-content h6 { 
              font-size: 0.67em; 
              font-weight: bold; 
              margin: 0.6em 0 0.3em 0; 
              color: #1a1a1a;
            }
            .word-content p { 
              margin: 0.6em 0;
              line-height: 1.8;
            }
            .word-content strong, .word-content b { 
              font-weight: bold; 
            }
            .word-content em, .word-content i { 
              font-style: italic; 
            }
            .word-content u { 
              text-decoration: underline; 
            }
            .word-content ul, .word-content ol { 
              margin: 0.8em 0; 
              padding-left: 2.5em;
            }
            .word-content li { 
              margin: 0.4em 0;
            }
            .word-content table { 
              border-collapse: collapse; 
              width: 100%; 
              margin: 1.2em 0;
              border: 1px solid #ddd;
            }
            .word-content table td, .word-content table th { 
              border: 1px solid #ddd; 
              padding: 8px 12px;
              text-align: left;
            }
            .word-content table th { 
              background-color: #f8f9fa; 
              font-weight: bold;
            }
            .word-content img { 
              max-width: 100%; 
              height: auto; 
              margin: 1.2em auto;
              display: block;
            }
            .word-content blockquote { 
              margin: 1em 0; 
              padding-left: 1em; 
              border-left: 4px solid #ddd; 
              color: #666;
            }
            .word-content code { 
              background-color: #f5f5f5; 
              padding: 2px 6px; 
              border-radius: 3px; 
              font-family: 'Consolas', 'Monaco', monospace;
              font-size: 0.9em;
            }
            .word-content pre { 
              background-color: #f5f5f5; 
              padding: 1em; 
              border-radius: 4px; 
              overflow-x: auto;
              margin: 1em 0;
            }
            .word-content a { 
              color: #0066cc; 
              text-decoration: none; 
            }
            .word-content a:hover { 
              text-decoration: underline; 
            }
          `}</style>
        </div>
      );
    }

    // 图片渲染
    if (fileType === 'image') {
      return (
        <div
          ref={imageContainerRef}
          className="h-full overflow-hidden bg-gray-100 flex items-center justify-center p-4 relative"
          onMouseDown={handleImageMouseDown}
          onDoubleClick={handleImageDoubleClick}
          style={{
            cursor: isDraggingImage
              ? 'grabbing'
              : imageScale > 1
                ? 'grab'
                : 'default',
          }}
        >
          <img
            src={displayFileUrl}
            alt={filename}
            className="max-w-full max-h-full object-contain rounded-lg shadow-lg select-none"
            style={{
              transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
              transformOrigin: 'center center',
              transition: isDraggingImage ? 'none' : 'transform 0.1s ease-out',
            }}
            draggable={false}
            onError={e => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const errorDiv = document.createElement('div');
              errorDiv.className = 'text-center text-gray-500';
              errorDiv.textContent = t('filePreview.failedToLoadImage');
              target.parentElement?.appendChild(errorDiv);
            }}
          />
          {imageScale !== 1 && (
            <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {Math.round(imageScale * 100)}%
            </div>
          )}
        </div>
      );
    }

    // PDF 渲染 - 使用 iframe 嵌入浏览器原生 PDF 查看器
    // 注意：不使用 object + iframe 嵌套，避免多次加载尝试
    if (fileType === 'pdf') {
      return (
        <div className="w-full h-full bg-gray-100">
          <iframe
            src={displayFileUrl}
            className="w-full h-full border-0"
            title={filename}
          />
        </div>
      );
    }

    // Markdown 渲染 - 使用 MarkdownPreviewPane 获得带导航的预览体验
    if (fileType === 'markdown') {
      if (showRawMarkdown) {
        // 🔥 Raw 模式使用 Monaco Editor（支持编辑）
        if ((projectId && filePath) || (clientId && filePath)) {
          return (
            <MonacoCodeEditor
              content={content}
              language="markdown"
              filename={filename}
              projectId={projectId}
              filePath={filePath}
              clientId={clientId}
              readOnly={readOnly}
              onSave={newContent => {
                // 🔥 保存成功后同步更新 content 状态，确保切换到预览模式时显示最新内容
                setContent(newContent);
                setRawContent(newContent);
              }}
            />
          );
        }
        // 只读模式
        return (
          <MonacoCodeEditor
            content={content}
            language="markdown"
            filename={filename}
            readOnly
          />
        );
      }
      return <MarkdownPreviewPane content={content} filename={filename} />;
    }

    // HTML 预览 - 在 iframe 中渲染
    if (fileType === 'markup') {
      return (
        <div className="h-full w-full">
          <iframe
            srcDoc={content}
            className="w-full h-full border-0"
            title={filename}
            sandbox="allow-scripts"
          />
        </div>
      );
    }

    // 🔥 JSON 格式化渲染 - 支持可编辑（当提供 projectId/clientId 和 filePath 时）
    if (fileType === 'data' && language === 'json' && formattedJsonContent) {
      // 🔥 如果提供了 projectId/clientId 和 filePath，使用 Monaco Editor（可编辑）
      if ((projectId && filePath) || (clientId && filePath)) {
        return (
          <MonacoCodeEditor
            content={formattedJsonContent}
            language="json"
            filename={filename}
            projectId={projectId}
            filePath={filePath}
            clientId={clientId}
            readOnly={readOnly}
            onSave={newContent => {
              // 🔥 保存成功后同步更新 content 状态
              setContent(newContent);
              setRawContent(newContent);
            }}
          />
        );
      }
      // 只读模式：使用虚拟滚动组件
      return (
        <VirtualCodeViewer content={formattedJsonContent} language="json" />
      );
    }
    // JSON 格式化渲染（备用路径）
    if (fileType === 'data' && language === 'json') {
      // 🔥 如果提供了 projectId/clientId 和 filePath，使用 Monaco Editor（可编辑）
      if ((projectId && filePath) || (clientId && filePath)) {
        try {
          const formatted = JSON.stringify(JSON.parse(content), null, 2);
          return (
            <MonacoCodeEditor
              content={formatted}
              language="json"
              filename={filename}
              projectId={projectId}
              filePath={filePath}
              clientId={clientId}
              readOnly={readOnly}
              onSave={newContent => {
                // 🔥 保存成功后同步更新 content 状态
                setContent(newContent);
                setRawContent(newContent);
              }}
            />
          );
        } catch (e) {
          return (
            <MonacoCodeEditor
              content={content}
              language="json"
              filename={filename}
              projectId={projectId}
              filePath={filePath}
              clientId={clientId}
              readOnly={readOnly}
              onSave={newContent => {
                // 🔥 保存成功后同步更新 content 状态
                setContent(newContent);
                setRawContent(newContent);
              }}
            />
          );
        }
      }
      // 只读模式
      try {
        const formatted = JSON.stringify(JSON.parse(content), null, 2);
        return <VirtualCodeViewer content={formatted} language="json" />;
      } catch (e) {
        return <VirtualCodeViewer content={content} language="json" />;
      }
    }

    // 代码高亮渲染
    if (
      fileType === 'code' ||
      fileType === 'sas' ||
      fileType === 'data' ||
      fileType === 'text'
    ) {
      // 🔥 如果提供了 projectId 和 filePath，或者有 clientId，使用 Monaco Editor（可编辑）
      if ((projectId && filePath) || (clientId && filePath)) {
        return (
          <MonacoCodeEditor
            content={content}
            language={language}
            filename={filename}
            projectId={projectId}
            filePath={filePath}
            clientId={clientId}
            readOnly={readOnly}
            onSave={newContent => {
              // 🔥 保存成功后同步更新 content 状态
              setContent(newContent);
              setRawContent(newContent);
            }}
          />
        );
      }
      // 否则使用虚拟滚动组件（只读）
      return <VirtualCodeViewer content={content} language={language} />;
    }

    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500 text-sm">
          {t('filePreview.previewNotAvailable')}
        </div>
      </div>
    );
  };

  // 🔥 根据 actualMode 确定容器样式（参考 ArtifactViewer 的实现）
  const containerClass = cn(
    // 🔥 全屏模式：覆盖整个屏幕
    isFullscreen
      ? 'fixed inset-0 z-[200] bg-white flex flex-col'
      : actualMode === 'split'
        ? // split 模式：作为 flex 子元素，相对定位
          'relative z-30 flex-shrink-0 h-full bg-white border-l border-gray-300 flex flex-col'
        : // overlay 模式：固定定位，覆盖在右侧（移动端占满除侧边栏外的区域）
          'fixed right-0 top-0 bottom-0 z-[100] bg-white border-l border-gray-300 shadow-2xl flex flex-col',
    // 拖拽时禁用文本选择
    isResizing && 'select-none'
  );

  // 🔥 移动端弹窗模式：宽度占满除侧边栏外的区域
  const containerStyle = useMemo(() => {
    // 🔥 全屏模式：覆盖整个屏幕
    if (isFullscreen) {
      return {
        width: '100%',
        minWidth: 'unset',
        maxWidth: 'unset',
      };
    }
    if (actualMode === 'overlay' && isMobile) {
      // 移动端：100% 宽度（侧边栏已折叠或不存在）
      return {
        width: '100%',
        minWidth: 'unset',
        maxWidth: 'unset',
        cursor: isResizing ? 'col-resize' : 'default',
      };
    }
    // PC端或分屏模式：使用百分比宽度
    return {
      width: `${width}%`,
      minWidth: '300px',
      maxWidth: '60%', // 🔥 限制最大宽度为 60%，避免左侧内容被过度压缩
      cursor: isResizing ? 'col-resize' : 'default',
    };
  }, [actualMode, isMobile, width, isResizing, isFullscreen]);

  // 🔥 如果没有文件信息，不渲染（在所有 hooks 之后，确保 React Hooks 规则）
  if (!fileUrl || !filename) {
    return null;
  }

  return (
    <div className={containerClass} style={containerStyle}>
      {/* 可拖拽的分隔条 - 全屏模式下隐藏 */}
      {!isFullscreen && (
        <div
          className="group w-2 absolute top-0 bottom-0 left-0 h-full cursor-col-resize -ml-1 z-50 grid place-items-center"
          onMouseDown={handleMouseDown}
        >
          {/* 细线 */}
          <div
            className={cn(
              'absolute top-0 bottom-0 right-1 w-[0.5px] transition-all',
              isResizing
                ? 'bg-blue-500 w-px translate-x-[0.5px]'
                : 'bg-gray-300 group-hover:delay-75 group-hover:bg-blue-400 group-hover:w-px group-hover:translate-x-[0.5px]'
            )}
          />
          {/* 圆形滑块指示器 */}
          <div
            className={cn(
              'h-6 w-2 relative rounded-full border bg-white shadow transition duration-200 cursor-col-resize',
              isResizing
                ? 'border-blue-500 bg-blue-500'
                : 'border-gray-300 group-hover:delay-75 group-hover:border-blue-500 group-hover:bg-blue-500'
            )}
          />
        </div>
      )}

      {/* 顶部工具栏 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0 lg:mt-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span
            className="text-sm font-medium text-gray-700 truncate"
            title={filename}
          >
            {filename}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* 宽度调整按钮 */}
          {/* <button
            onClick={() => setWidth(Math.max(30, width - 10))}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
            title="Decrease width"
          >
            <ChevronRight size={18} />
          </button>
          <button
            onClick={() => setWidth(Math.min(80, width + 10))}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
            title="Increase width"
          >
            <ChevronLeft size={18} />
          </button> */}

          {/* Markdown 原文切换按钮 - 仅对 markdown 文件显示 */}
          {fileType === 'markdown' && (
            <button
              onClick={() => setShowRawMarkdown(!showRawMarkdown)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition-colors',
                showRawMarkdown
                  ? 'text-blue-600 bg-blue-100 hover:bg-blue-200'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-200'
              )}
              title={
                showRawMarkdown
                  ? t('filePreview.showRenderedPreview')
                  : t('filePreview.showRawMarkdown')
              }
            >
              <FileCode size={16} />
              <span>
                {showRawMarkdown
                  ? t('filePreview.preview')
                  : t('filePreview.raw')}
              </span>
            </button>
          )}

          {/* CSV 原文切换按钮 - 仅对 CSV/TSV 文件显示 */}
          {csvData && (
            <button
              onClick={() => setShowRawCsv(!showRawCsv)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition-colors',
                showRawCsv
                  ? 'text-blue-600 bg-blue-100 hover:bg-blue-200'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-200'
              )}
              title={
                showRawCsv
                  ? t('filePreview.showTablePreview')
                  : t('filePreview.showRawCsv')
              }
            >
              <FileCode size={16} />
              <span>
                {showRawCsv ? t('filePreview.preview') : t('filePreview.raw')}
              </span>
            </button>
          )}

          {/* 复制按钮 - 图片文件不显示 */}
          {fileType !== 'image' && (
            <button
              onClick={handleCopy}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-sm   rounded transition-colors',
                copied
                  ? 'text-green-600 bg-green-100 hover:bg-green-200'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-200'
              )}
              title={
                copied
                  ? t('filePreview.copied')
                  : t('filePreview.copyRawContent')
              }
            >
              {copied ? (
                <Check size={16} className="text-green-600" />
              ) : (
                <Copy size={16} />
              )}
              <span>
                {copied ? t('filePreview.copied') : t('filePreview.copy')}
              </span>
            </button>
          )}
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
            title={t('filePreview.downloadFileTooltip')}
          >
            <Download size={16} />
            <span>{t('filePreview.download')}</span>
          </button>

          {/* 🔥 全屏切换按钮 - 移动设备不显示（移动设备默认全屏且不允许切换） */}
          {!isMobile && (
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
              title={
                isFullscreen
                  ? t('filePreview.exitFullscreen')
                  : t('filePreview.fullscreen')
              }
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
          )}

          {/* 关闭按钮 */}
          <button
            onClick={handleClose}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors"
            title={t('filePreview.closePreview')}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* 内容区域 */}
      <div
        className={cn(
          'flex-1 overflow-hidden bg-white',
          isResizing && 'pointer-events-none'
        )}
      >
        {renderContent()}
      </div>
    </div>
  );
}
